// LOT0F2A_PRP_FORFY_PULSE_AGENTS_REGISTRY_V2
//
// Dérivé de forfeo-pulse-main(8).zip:
// apps/backend/src/services/agents/agentRegistry.ts
//
// Frontend-safe: métadonnées, contrats de sortie et garde-fous seulement.
// Les prompts serveur complets restent hors navigateur.

export type PrpForfyAgentKey =
  | "forfy_prp_analyst"
  | "forfy_prp_qualifier"
  | "forfy_prp_writer"
  | "forfy_prp_responder"
  | "forfy_prp_closer"
  | "forfy_prp_onboarder"
  | "forfy_prp_marketer"
  | "forfy_prp_scout";

export type PrpForfyAgent = {
  key: PrpForfyAgentKey;
  pulseAgentKey: string;
  pulseAgentName: string;
  pulseSquad: string;
  name: string;
  description: string;
  recommendedUse: string;
  outputContract: string[];
  requiresHumanApproval: true;
};

const guardrails = [
  "Préparation assistive seulement.",
  "Aucun envoi automatique.",
  "Aucune publication automatique.",
  "Aucune validation de coordonnée automatique.",
  "Aucune autorité transactionnelle.",
  "Validation humaine requise avant action externe.",
] as const;

export const PRP_FORFY_GUARDRAILS = guardrails;

export const PRP_FORFY_AGENTS: PrpForfyAgent[] = [
  {
    key: "forfy_prp_analyst",
    pulseAgentKey: "pulse_analyst",
    pulseAgentName: "Pulse Analyst",
    pulseSquad: "Acquisition Interne",
    name: "Forfy Analyste PRP",
    description:
      "Analyse le potentiel d’un commerce local et donne un score de compatibilité Forfeo.",
    recommendedUse:
      "Prioriser les commerces sauvegardés ou assignés avant un appel ou une visite.",
    outputContract: [
      "score",
      "priority",
      "product_fit",
      "opportunity_summary",
      "weaknesses",
      "recommended_angle",
    ],
    requiresHumanApproval: true,
  },
  {
    key: "forfy_prp_qualifier",
    pulseAgentKey: "pulse_qualifier",
    pulseAgentName: "Pulse Qualifier",
    pulseSquad: "Acquisition Interne",
    name: "Forfy Qualificateur PRP",
    description:
      "Qualifie rapidement un prospect et recommande la meilleure prochaine action.",
    recommendedUse:
      "Décider si le prospect doit être contacté maintenant, relancé plus tard ou mis en veille.",
    outputContract: [
      "qualification_status",
      "reason",
      "missing_information",
      "recommended_next_action",
      "suggested_agent",
    ],
    requiresHumanApproval: true,
  },
  {
    key: "forfy_prp_writer",
    pulseAgentKey: "pulse_writer",
    pulseAgentName: "Pulse Writer",
    pulseSquad: "Acquisition Interne",
    name: "Forfy Rédacteur PRP",
    description:
      "Prépare un courriel d’approche personnalisé destiné à une validation humaine.",
    recommendedUse:
      "Créer une première approche locale, courte et professionnelle.",
    outputContract: [
      "subject",
      "body",
      "tone_used",
      "target_email",
    ],
    requiresHumanApproval: true,
  },
  {
    key: "forfy_prp_responder",
    pulseAgentKey: "pulse_responder",
    pulseAgentName: "Pulse Responder",
    pulseSquad: "Acquisition Interne",
    name: "Forfy Répondeur PRP",
    description:
      "Analyse les objections du prospect et prépare une stratégie de relance.",
    recommendedUse:
      "Préparer une réponse respectueuse et une prochaine étape claire.",
    outputContract: [
      "objection_analysis",
      "counter_arguments",
      "follow_up_email",
      "next_step",
    ],
    requiresHumanApproval: true,
  },
  {
    key: "forfy_prp_closer",
    pulseAgentKey: "pulse_closer",
    pulseAgentName: "Pulse Closer",
    pulseSquad: "Conversion",
    name: "Forfy Closer PRP",
    description:
      "Prépare un argumentaire de conversion pour transformer un prospect chaud en partenaire.",
    recommendedUse:
      "Structurer la valeur, les objections probables et la prochaine action.",
    outputContract: [
      "value_summary",
      "closing_message",
      "likely_objections",
      "short_responses",
      "next_action",
    ],
    requiresHumanApproval: true,
  },
  {
    key: "forfy_prp_onboarder",
    pulseAgentKey: "pulse_onboarder",
    pulseAgentName: "Pulse Onboarder",
    pulseSquad: "Activation Partenaire",
    name: "Forfy Onboarder Partenaire",
    description:
      "Prépare les prochaines étapes après qu’un commerce accepte d’avancer avec Forfeo.",
    recommendedUse:
      "Créer une checklist d’accueil et les informations à collecter.",
    outputContract: [
      "onboarding_checklist",
      "information_to_collect",
      "welcome_message",
      "admin_next_steps",
      "local_launch_suggestion",
    ],
    requiresHumanApproval: true,
  },
  {
    key: "forfy_prp_marketer",
    pulseAgentKey: "pulse_marketer",
    pulseAgentName: "Pulse Marketer",
    pulseSquad: "Marketing & Contenu",
    name: "Forfy Marketer local",
    description:
      "Prépare du contenu social ciblé pour promouvoir le commerce après validation.",
    recommendedUse:
      "Préparer des brouillons de contenu local pour approbation.",
    outputContract: [
      "instagram_caption",
      "facebook_post",
      "suggested_hashtags",
      "visual_idea",
      "image_prompt",
    ],
    requiresHumanApproval: true,
  },
  {
    key: "forfy_prp_scout",
    pulseAgentKey: "pulse_scout",
    pulseAgentName: "Pulse Scout",
    pulseSquad: "Prospection Externe",
    name: "Forfy Scout local",
    description:
      "Recherche et structure des cibles commerciales pertinentes pour la prospection locale.",
    recommendedUse:
      "Préparer l’onglet Autour de moi et les listes de commerces à sauvegarder.",
    outputContract: [
      "prospects",
      "summary",
    ],
    requiresHumanApproval: true,
  },
];

export type PrpForfyPreviewContext = {
  businessName?: string;
  city?: string;
  category?: string;
  status?: string;
  region?: string;
};

export function buildPrpForfyLocalPreview(
  agentKey: PrpForfyAgentKey,
  context: PrpForfyPreviewContext,
): Record<string, unknown> {
  const businessName =
    context.businessName?.trim() || "Commerce local";
  const city =
    context.city?.trim() || "Québec";
  const category =
    context.category?.trim() || "Commerce local";
  const status =
    context.status?.trim() || "à qualifier";
  const region =
    context.region?.trim() || "Capitale-Nationale";

  if (agentKey === "forfy_prp_analyst") {
    return {
      score: category.toLowerCase().includes("spa") ? 82 : 72,
      priority: category.toLowerCase().includes("spa") ? "high" : "medium",
      product_fit: ["Forfeo Explore", "Forfeo Shop", "Forfeo Pulse"],
      opportunity_summary:
        `${businessName} est un prospect local pertinent à ${city}, catégorie ${category}.`,
      weaknesses: [
        status.toLowerCase().includes("enrichissement")
          ? "Informations de localisation ou de contact à compléter."
          : "Informations commerciales à compléter.",
      ],
      recommended_angle:
        "Positionner Forfeo comme un écosystème local de découverte, de croissance et d’activation commerciale.",
    };
  }

  if (agentKey === "forfy_prp_qualifier") {
    return {
      qualification_status:
        status.toLowerCase().includes("enrichissement") ? "incomplete" : "warm",
      reason:
        `${businessName} est dans le territoire ${region}; la fiche doit être suffisamment complète avant une approche commerciale structurée.`,
      missing_information: [
        "Adresse officielle",
        "Contact décisionnaire",
        "Courriel professionnel",
        "Offre ou expérience potentielle",
      ],
      recommended_next_action:
        "Compléter la fiche prospect, puis préparer un premier contact humain.",
      suggested_agent: "forfy_prp_writer",
    };
  }

  if (agentKey === "forfy_prp_writer") {
    return {
      subject: `Collaboration locale avec ${businessName}`,
      body:
        `Bonjour,\n\nJe vous contacte parce que ${businessName} semble bien positionné dans l’offre locale de ${city}. Forfeo développe un écosystème qui aide les commerces locaux à structurer leur croissance et leur présence auprès des clients et voyageurs.\n\nJ’aimerais vous présenter brièvement l’approche et voir si une courte discussion pourrait être pertinente.\n\nMerci,\nL’équipe Forfeo`,
      tone_used: "professionnel, local, humain, non agressif",
      target_email: "",
    };
  }

  if (agentKey === "forfy_prp_responder") {
    return {
      objection_analysis:
        "Le commerce peut manquer de temps, ne pas percevoir la valeur immédiate ou craindre une solution trop lourde.",
      counter_arguments: [
        "Commencer simplement avec une courte discussion.",
        "Le commerce conserve le contrôle de ses décisions.",
        "Forfeo complète les outils existants.",
      ],
      follow_up_email:
        `Bonjour,\n\nJe comprends. L’objectif est simplement de voir si une approche locale et légère pourrait être utile pour ${businessName}. Une courte discussion suffirait pour valider la pertinence.\n\nMerci,`,
      next_step: "Proposer une courte discussion et laisser le commerce choisir.",
    };
  }

  if (agentKey === "forfy_prp_closer") {
    return {
      value_summary:
        `${businessName} pourrait utiliser Forfeo pour structurer une présence locale plus forte et préparer une offre claire.`,
      closing_message:
        "La prochaine étape est de confirmer les informations de base et de préparer une première fiche partenaire à faire approuver.",
      likely_objections: [
        "Pas le temps",
        "Retour incertain",
        "Déjà présent ailleurs",
      ],
      short_responses: [
        "On peut commencer petit.",
        "La présence Forfeo est complémentaire.",
        "Le commerce conserve le contrôle.",
      ],
      next_action: "Planifier une courte rencontre de validation.",
    };
  }

  if (agentKey === "forfy_prp_onboarder") {
    return {
      onboarding_checklist: [
        "Confirmer le nom public du commerce.",
        "Identifier le contact principal.",
        "Collecter l’adresse officielle.",
        "Définir une première offre ou expérience.",
        "Soumettre à validation admin.",
      ],
      information_to_collect: [
        "Adresse officielle",
        "Courriel professionnel",
        "Téléphone",
        "Logo ou photo",
        "Description courte",
      ],
      welcome_message:
        `Bienvenue ${businessName}. L’intégration Forfeo se fera progressivement avec validation humaine.`,
      admin_next_steps: [
        "Vérifier la fiche commerce.",
        "Valider les informations nécessaires.",
        "Approuver la publication.",
      ],
      local_launch_suggestion:
        "Commencer avec une offre locale simple et facile à expliquer.",
    };
  }

  if (agentKey === "forfy_prp_marketer") {
    return {
      instagram_caption:
        `${businessName} fait partie des commerces locaux à découvrir à ${city}.`,
      facebook_post:
        `Découvrir les commerces locaux, c’est aussi soutenir les expériences de proximité. ${businessName}, à ${city}, peut s’inscrire dans une sélection Forfeo locale.`,
      suggested_hashtags: [
        "#Forfeo",
        "#AchatLocal",
        "#QuebecLocal",
        "#ExperienceLocale",
      ],
      visual_idea:
        "Photo réaliste et premium du commerce ou de l’expérience.",
      image_prompt:
        "premium realistic photo of a local Quebec business, warm lighting, welcoming atmosphere, professional commercial photography",
    };
  }

  return {
    prospects: [
      {
        business_name: businessName,
        city,
        category,
        email: "",
      },
    ],
    summary:
      `Forfy Scout local prépare la prospection autour de ${region}. Le lot Autour de moi alimentera cette liste via une recherche locale contrôlée.`,
  };
}
